import { motion } from 'framer-motion';
import { MessageSquare, GitBranch, Clock, Zap, X, Check } from 'lucide-react';

export function ProblemGap() {
  return (
    <section className="py-24 bg-surface-900">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            Two Ways to Work with AI
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Most tools give you a chatbot. We give you an orchestration engine.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* The Chatbot Way */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-2xl blur-lg" />
            <div className="relative bg-surface-800 rounded-xl border border-red-500/30 p-8 h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <MessageSquare className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary">The Chatbot Way</h3>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-text-secondary">One task at a time, waiting for tokens</span>
                </div>
                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-text-secondary">Context lost between sessions</span>
                </div>
                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-text-secondary">No visibility into what the AI is thinking</span>
                </div>
                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-text-secondary">Your code sent to the cloud</span>
                </div>
              </div>

              {/* Visual: Linear chat */}
              <div className="bg-surface-900/50 rounded-lg p-4 border border-surface-700">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-surface-600" />
                    <div className="flex-1 h-4 bg-surface-700 rounded" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-text-tertiary animate-pulse" />
                    <span className="text-xs text-text-tertiary">Generating...</span>
                  </div>
                  <div className="flex items-center gap-2 opacity-40">
                    <div className="w-6 h-6 rounded-full bg-surface-600" />
                    <div className="flex-1 h-4 bg-surface-700 rounded" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* The Orchestration Way */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-primary-500/20 to-accent-500/20 rounded-2xl blur-lg" />
            <div className="relative bg-surface-800 rounded-xl border border-primary-500/30 p-8 h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-primary-500/10">
                  <GitBranch className="w-5 h-5 text-primary-400" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary">The Orchestration Way</h3>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-success-400 mt-0.5 shrink-0" />
                  <span className="text-text-secondary">Parallel agents working concurrently</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-success-400 mt-0.5 shrink-0" />
                  <span className="text-text-secondary">Persistent context across sessions</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-success-400 mt-0.5 shrink-0" />
                  <span className="text-text-secondary">Human approval at decision points</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-success-400 mt-0.5 shrink-0" />
                  <span className="text-text-secondary">Local-first, snippets-only to LLM</span>
                </div>
              </div>

              {/* Visual: DAG */}
              <div className="bg-surface-900/50 rounded-lg p-4 border border-surface-700">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-success-400" />
                      <span className="text-xs text-text-tertiary">Agent 1</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-primary-400 animate-pulse" />
                      <span className="text-xs text-text-tertiary">Agent 2</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-primary-400 animate-pulse" />
                      <span className="text-xs text-text-tertiary">Agent 3</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-primary-400" />
                    <span className="text-xs text-primary-400 font-medium">3x parallel</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
